// 양식 변환 진행 표시 — 모달을 닫아도 Job이 계속 도는 만큼, 헤더에 "지금 뭐가 돌고 있나"를
// 남긴다. 모달이 유일한 표시자였을 때는 창을 닫는 순간 진행이 보이지 않아 닫기 자체를 막아야
// 했다(최대 5분 동안 에디터가 잠긴다).
//
// 실패는 여기에 싣지 않는다 — 토스트가 이미 알리므로 중복이고, 배지는 "진행 중/받아갈 결과가
// 있음"만 담당한다(TemplateIssuesBadge가 경고 0이면 렌더하지 않는 것과 같은 정책).

import { formatElapsed } from '../../hooks/useElapsedSeconds';
import type { FormImportJobState } from './useFormImportJob';

export function FormImportJobPill({
  job,
  onOpen,
}: {
  job: FormImportJobState;
  /** 클릭 — 모달을 열어 진행 상황·결과를 보여 준다. */
  onOpen: () => void;
}) {
  if (job.phase === 'importing') {
    const stage = job.progress?.stage ?? 'uploading';
    const batch = job.progress?.batch;
    // AI 응답 대기 구간(analyzing)의 percent는 한 항목 안에서 멈춰 있다 — 서버가 그 구간의
    // 진행을 모른다. 멈춘 숫자를 보이면 "죽었나"로 읽히므로 경과 시간만 쓴다(모달의 불확정
    // 바와 같은 판단). 나머지 stage에서는 percent가 정직하므로 함께 보인다.
    //
    // 단, **배치는 analyzing이어도 k/N이 정직하다** — 몇 번째 시트인지는 서버가 안다.
    // 40분짜리 작업에서 이 숫자가 없으면 진행이 보이지 않아 취소를 누르게 된다.
    const determinate = stage !== 'analyzing';
    const percent = Math.min(100, Math.max(0, job.progress?.percent ?? 5));
    const elapsed = formatElapsed(job.elapsedSeconds);
    const cancelling = job.progress?.cancelRequested === true;
    const label = cancelling ? '취소 중' : '양식 변환 중';
    const title = cancelling
      ? '취소를 요청했습니다 — 처리 중인 시트가 끝나면 중단됩니다.'
      : batch
        ? `양식 변환이 진행 중입니다 (${batch.current}/${batch.total}번째 시트${batch.currentSheetName ? ` · ${batch.currentSheetName}` : ''}, ${elapsed} 경과) — 클릭하면 진행 상황이 열립니다.`
        : `양식 변환이 진행 중입니다 (${elapsed} 경과) — 클릭하면 진행 상황이 열립니다.`;
    return (
      <button
        type="button"
        onClick={onOpen}
        title={title}
        className={
          cancelling
            ? 'inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100'
            : 'inline-flex items-center gap-1.5 rounded-full border border-brand-300 bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100'
        }
      >
        <span
          className={
            cancelling
              ? 'inline-block h-1.5 w-1.5 rounded-full bg-gray-400'
              : 'inline-block h-1.5 w-1.5 rounded-full bg-brand-500 motion-safe:animate-pulse'
          }
          aria-hidden
        />
        {label}
        {batch ? (
          <span className="tabular-nums font-normal">
            {batch.current}/{batch.total}
            {batch.currentSheetName ? (
              // 시트 이름은 길 수 있다 — pill이 헤더를 밀어내지 않게 잘라 준다(전체는 title에).
              <span className="ml-1 inline-block max-w-[8rem] truncate align-bottom">
                {batch.currentSheetName}
              </span>
            ) : null}
          </span>
        ) : null}
        <span className="tabular-nums font-normal">
          {determinate || batch ? `${percent}% · ${elapsed}` : elapsed}
        </span>
      </button>
    );
  }

  if (job.phase === 'result') {
    return (
      <button
        type="button"
        onClick={onOpen}
        title="변환된 템플릿 초안이 있습니다 — 클릭하면 결과가 열립니다."
        className="inline-flex items-center gap-1.5 rounded-full border border-green-300 bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 hover:bg-green-100"
      >
        ✓ 변환 완료 · 결과 보기
      </button>
    );
  }

  return null;
}
